import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader } from '../../../components/ui/card'
import { Trash2, TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { formatMeasurement as formatPercent } from '../../../lib/phoenixUtils'
import { useUnits } from '../../../contexts/UnitsContext'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import PendingSyncBadge from '../../../components/PendingSyncBadge'

const FIELDS = ['neck', 'chest', 'belly', 'waist', 'arm', 'thigh']

export function MeasurementCard({ measurement, onDelete }) {
  const { t } = useTranslation()
  const { lengthUnit, formatLength } = useUnits()
  const confirm = useConfirm()

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('phoenixTracker.measurements.confirmDelete'),
      description: t('phoenixTracker.measurements.confirmDeleteDesc'),
      confirmLabel: t('phoenixTracker.measurements.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(measurement.id)
  }

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <span className="font-mono text-sm text-muted-foreground flex items-center gap-1.5">
          {new Date(measurement.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          <PendingSyncBadge mutation={measurement._pending} />
        </span>
        <button
          onClick={handleDelete}
          className="p-2 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
          aria-label={t('phoenixTracker.measurements.delete')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {FIELDS.map((field) => {
            const value = measurement[field]
            const diff = measurement.diffs?.[field]
            const diffColor = diff === null || diff === undefined
              ? ''
              : diff < 0
                ? 'text-green-500'
                : diff > 0
                  ? 'text-red-500'
                  : 'text-muted-foreground'

            return (
              <div key={field} className="text-center p-2 rounded-lg bg-accent/50">
                <p className="text-xs text-muted-foreground mb-1">
                  {t(`phoenixTracker.measurements.${field}`)}
                </p>
                <p className="font-mono font-semibold text-foreground">
                  {formatLength(value)}
                  {value != null && (
                    <span className="text-xs text-muted-foreground ms-1">
                      {lengthUnit}
                    </span>
                  )}
                </p>
                {diff != null && (
                  <div className={cn('flex items-center justify-center gap-0.5 text-xs font-mono mt-0.5', diffColor)}>
                    {diff < 0 ? <TrendingDown className="w-3 h-3" /> : diff > 0 ? <TrendingUp className="w-3 h-3" /> : null}
                    <span>{diff > 0 ? '+' : ''}{formatLength(diff)}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {measurement.body_fat != null && (() => {
          const bfDiff = measurement.diffs?.body_fat
          const bfDiffColor = bfDiff === null || bfDiff === undefined
            ? ''
            : bfDiff < 0
              ? 'text-green-500'
              : bfDiff > 0
                ? 'text-red-500'
                : 'text-muted-foreground'
          return (
            <div className="mt-3 flex items-center justify-between p-2 rounded-lg bg-purple-100/50 dark:bg-purple-900/20">
              <span className="text-xs text-muted-foreground">{t('phoenixTracker.measurements.bodyFat')}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-foreground">
                  {formatPercent(measurement.body_fat)}<span className="text-xs text-muted-foreground ms-1">%</span>
                </span>
                {bfDiff != null && (
                  <div className={cn('flex items-center gap-0.5 text-xs font-mono', bfDiffColor)}>
                    {bfDiff < 0 ? <TrendingDown className="w-3 h-3" /> : bfDiff > 0 ? <TrendingUp className="w-3 h-3" /> : null}
                    <span>{bfDiff > 0 ? '+' : ''}{formatPercent(bfDiff)}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })()}
        {measurement.note && (
          <p className="mt-3 text-xs italic text-muted-foreground whitespace-pre-wrap break-words">
            {measurement.note}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
